import { useMemo, useState } from "react";
import { Feature } from "ol";
import WKT from "ol/format/WKT";
import Geometry from "ol/geom/Geometry";
import Circle from "ol/style/Circle";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Style, { StyleLike } from "ol/style/Style";
import { CheckBoxEl } from "@avinet/adaptive-ui-core/form/controls/CheckBox";
import Grow from "@avinet/adaptive-ui-core/layout/Grow";
import Row from "@avinet/adaptive-ui-core/layout/Row";
import ClusterSource from "@avinet/adaptive-ui-maps/vector/source/ClusterSource";
import VectorSource from "@avinet/adaptive-ui-maps/vector/source/VectorSource";
import clusterStyle from "@avinet/adaptive-ui-maps/vector/style/clusterStyle";
import clusterText from "@avinet/adaptive-ui-maps/vector/style/clusterText";
import defaultStyle from "@avinet/adaptive-ui-maps/vector/style/defaultStyle";
import VectorLayer from "@avinet/adaptive-ui-maps/vector/VectorLayer";
import { GeometrySection, GeometrySectionType } from "../../types/Section";
import { derivedHue } from "../../utils/color";
import { ResultsFilter, ResultsFilters, useFilteredData } from "./Results";

export interface GeometryResultsRendererProps {
  section: GeometrySection;
  data: any[];
  filters: ResultsFilters;
  setFilter: (id: string, filter?: ResultsFilter) => void;
}

const GeometryResults = ({
  section,
  data,
  filters,
}: GeometryResultsRendererProps) => {
  const filteredData = useFilteredData(section.columnName, filters, data);

  const features = useMemo(() => {
    const wkt = new WKT();

    const features = data
      .map((record) => {
        const geom = record[section.columnName];
        if (!geom) return undefined;

        const f = wkt.readFeature(geom) as Feature<Geometry>;
        if (!f) return undefined;

        f.getGeometry()?.transform("EPSG:4326", "EPSG:3857");
        f.set("id", record["id"]);

        return f;
      })
      .filter((f) => !!f);

    return features as Feature<Geometry>[];
  }, [section, data]);

  const filteredFeatures = useMemo(() => {
    return features.filter(
      (f) => !!filteredData.find((record) => record["id"] === f?.get("id"))
    );
  }, [filteredData, features]);

  const hue = derivedHue(section.columnName);

  const [zIndex, style]: [number, StyleLike] = useMemo(() => {
    const color = `hsl(${hue}, 60%, 40%)`;
    const colorA = `hsla(${hue}, 60%, 40%, 0.4)`;
    console.log(color, colorA);
    switch (section.type) {
      case GeometrySectionType.geometry_point:
        return [
          10,
          clusterStyle({
            featureStyle: () => [
              new Style({
                image: new Circle({
                  radius: 7,
                  fill: new Fill({ color: color }),
                  stroke: new Stroke({ color: "#FFF", width: 2 }),
                }),
              }),
            ],
            clusterStyle: defaultStyle({
              labelText: clusterText({ color: "#fff", offsetY: 1 }),
              fillColor: color,
              strokeColor: "#fff",
              strokeWidth: 2,
            }),
          }),
        ];

      case GeometrySectionType.geometry_line: {
        const line = new Style({
          stroke: new Stroke({ color: color, width: 3 }),
        });
        const outline = new Style({
          stroke: new Stroke({ color: "#fff", width: 5 }),
        });

        return [9, [outline, line]];
      }

      case GeometrySectionType.geometry_polygon: {
        const style = new Style({
          fill: new Fill({ color: colorA }),
          stroke: new Stroke({ color: color, width: 3 }),
        });
        const outline = new Style({
          stroke: new Stroke({ color: "#fff", width: 5 }),
        });

        return [8, [outline, style]];
      }
    }
  }, [section.type]);

  const [enabled, setEnabled] = useState(true);

  return (
    <>
      <Row>
        <Grow>
          <CheckBoxEl
            id="enabled"
            label={section.label}
            value={enabled}
            onChange={setEnabled}
          />
        </Grow>
        <span
          style={{
            backgroundColor: `hsl(${hue}, 60%, 40%)`,
            minWidth: 16,
            minHeight: 16,
            borderRadius: 8,
          }}
        />
      </Row>
      {enabled && (
        <VectorLayer zIndex={zIndex} style={style}>
          {!!filteredFeatures &&
            (section.type === GeometrySectionType.geometry_point ? (
              <ClusterSource>
                <VectorSource features={filteredFeatures} />
              </ClusterSource>
            ) : (
              <VectorSource features={filteredFeatures} />
            ))}
        </VectorLayer>
      )}
    </>
  );
};

export default GeometryResults;
